import { apiSlice } from "@/services/api-slice";
import { APIResponse } from "@/types/custom-http";
import {
  SystemSetting,
  CreateSystemSetting,
  UpdateSystemSetting,
} from "./systems-settings.interface";

export const systemSettingsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSystemSettings: builder.query<APIResponse<SystemSetting[]>, void>({
      query: () => ({ url: "auth/systems-settings" }),
      providesTags: ["SystemSettings"],
    }),

    getSystemSettingById: builder.query<APIResponse<SystemSetting>, number>({
      query: (id) => ({ url: `auth/systems-settings/${id}` }),
      providesTags: (_result, _error, id) => [{ type: "SystemSettings", id }],
    }),

    getSystemSettingsBySection: builder.query<APIResponse<SystemSetting[]>, string>({
      query: (section) => ({ url: `auth/systems-settings/section/${section}` }),
      providesTags: ["SystemSettings"],
    }),

    createSystemSetting: builder.mutation<APIResponse<SystemSetting>, CreateSystemSetting>({
      query: (data) => ({
        url: "auth/systems-settings",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["SystemSettings"],
    }),

    updateSystemSetting: builder.mutation<APIResponse<SystemSetting>, { id: number; changes: UpdateSystemSetting }>({
      query: ({ id, changes }) => ({
        url: `auth/systems-settings/${id}`,
        method: "PATCH",
        body: changes,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "SystemSettings", id }, "SystemSettings"],
    }),

    deleteSystemSetting: builder.mutation<APIResponse<null>, number>({
      query: (id) => ({
        url: `auth/systems-settings/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [{ type: "SystemSettings", id }, "SystemSettings"],
    }),

    updateSystemSettingsBatch: builder.mutation<
      APIResponse<SystemSetting[]>,
      { section: string; items: Array<{ id: number; value_text: string; is_active: boolean }> }
    >({
      query: ({ section, items }) => ({
        url: `auth/systems-settings/section/${section}/batch`,
        method: "POST",
        body: { items },
      }),
      invalidatesTags: ["SystemSettings"],
    }),
  }),
});

export const {
  useGetSystemSettingsQuery,
  useLazyGetSystemSettingsQuery,
  useGetSystemSettingByIdQuery,
  useLazyGetSystemSettingByIdQuery,
  useGetSystemSettingsBySectionQuery,
  useLazyGetSystemSettingsBySectionQuery,
  useCreateSystemSettingMutation,
  useUpdateSystemSettingMutation,
  useDeleteSystemSettingMutation,
  useUpdateSystemSettingsBatchMutation,
} = systemSettingsApi;
